import React from "react";
import { useTranslation } from "react-i18next";

import { showNotification } from "fe-shared/src/components";
import { CloseIcon, SupportingDocsIcon } from "fe-shared/src/components/Icons";
import { downloadFile } from "fe-shared/src/helpers/files";
import { PickupRequestDocument } from "../../types";

import { getDocumentDownloadPath } from "../../api/pickupRequests";
import isImageDocument from "../../helpers/documents";
import useDocumentImageUrl from "../../hooks/useDocumentImageUrl";

import styles from "./RequestDocumentThumbnail.module.scss";

interface Props {
  requestNumber: string;
  document: PickupRequestDocument;
  onDelete?: (documentId: number) => void;
  isDeleting?: boolean;
}

const RequestDocumentThumbnail = (props: Props) => {
  const { requestNumber, document: doc, onDelete, isDeleting = false } = props;
  const { t } = useTranslation();
  const isImage = isImageDocument(doc.name);
  const path = getDocumentDownloadPath(requestNumber, doc.documentId);
  const imageUrl = useDocumentImageUrl(isImage ? path : null);

  const onClick = () => {
    downloadFile(path, doc.name, 0).catch(() => {
      showNotification(
        t("pickupRequests.details.documentDownloadError"),
        undefined,
        CloseIcon
      );
    });
  };

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.thumbnail}
        aria-label={t("pickupRequests.details.downloadDocument", {
          name: doc.name,
        })}
        onClick={onClick}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={t("pickupRequests.details.documentThumbnailAlt", {
              name: doc.name,
            })}
            className={styles.image}
          />
        ) : (
          <SupportingDocsIcon width="32" height="32" fillColor="#B5BDC2" />
        )}
        <span className={styles.name}>{doc.name}</span>
      </button>
      {onDelete && doc.isDeletable && (
        <button
          type="button"
          className={styles.remove}
          disabled={isDeleting}
          aria-label={t("pickupRequests.details.removeDocument", {
            name: doc.name,
          })}
          onClick={() => onDelete(doc.documentId)}
        >
          <CloseIcon width="12" height="12" />
        </button>
      )}
    </div>
  );
};

export default RequestDocumentThumbnail;
